/**
 * Hook qui synthetise le statut FREE de l'user :
 *  - isActive             : essai en cours (ou plan paye)
 *  - isExpired            : essai termine (FREE strict + free_expires_at < now)
 *  - expiresAt            : Date object ou null
 *  - daysLeft             : nb jours restants jusqu'a expiration (negatif si depasse)
 *  - formattedExpiresAt   : 'X juin 2026' (locale FR)
 *  - isFreeUser           : booleen, plan == 'FREE' (utile pour bandeau dynamique)
 *
 * Source : user.free_expires_at expose par GET /api/auth/me.
 * Pour les plans payants ou superadmin, free_expires_at est NULL et
 * isExpired/isActive renvoient false / true.
 */
import { useAuth } from '../context/AuthContext'
import { estMembreDeClub } from '../utils/planAccess'

export function useFreeStatus() {
  const { user } = useAuth()

  const empty = {
    isActive: true,
    isExpired: false,
    expiresAt: null,
    daysLeft: null,
    formattedExpiresAt: null,
    isFreeUser: false,
  }

  if (!user) return empty
  if (user.is_superadmin) return empty

  // MEMBRE-CLUB-002 · UN COACH DE CLUB N'EST PAS UN COMPTE D'ESSAI.
  //
  // Son plan personnel affiche FREE et son `free_expires_at` continue de
  // courir, alors qu'il est couvert par l'abonnement de son club. Ce hook
  // gouverne QUATRE verrous durs — creation de match (CreateMatch), seances
  // (SeancesPage), effectif (PlayerManagement) et le bandeau d'expiration.
  //
  // Sans cette sortie, tous les coachs de club perdaient ces trois ecrans le
  // jour ou leur `free_expires_at` passait, sans que rien ne l'annonce et sans
  // qu'aucun test ne le voie · la date etait dans le futur au moment ou le
  // defaut a ete introduit. Pour le compte GFCA, c'etait le 20/08/2026.
  if (estMembreDeClub(user)) return empty

  const plan = (user.plan || '').toUpperCase()
  const isFreeUser = plan === 'FREE'

  if (!user.free_expires_at) {
    return { ...empty, isFreeUser }
  }

  const expiresAt = new Date(user.free_expires_at)
  if (Number.isNaN(expiresAt.getTime())) return { ...empty, isFreeUser }

  const now = new Date()
  const isExpired = isFreeUser && expiresAt < now
  const msPerDay = 24 * 60 * 60 * 1000
  const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / msPerDay)
  const formattedExpiresAt = expiresAt.toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric',
  })

  return {
    isActive: !isExpired,
    isExpired,
    expiresAt,
    daysLeft,
    formattedExpiresAt,
    isFreeUser,
  }
}
